import RoundedCard from "@cle/components/RoundedCard";
import { NON_TRANSFERABLE_LABEL } from "@cle/utils/injunctionReportNotices";
import type { InjunctionReportOverviewRow } from "@cle/utils/calculateInjunctionCategorySummaries";
import { formatLessons } from "@cle/utils/lessonLimits";

export type { InjunctionReportOverviewRow } from "@cle/utils/calculateInjunctionCategorySummaries";

type InjunctionReportOverviewProps = {
  rows: InjunctionReportOverviewRow[];
  variant: "pending" | "submitted";
  header: string;
  pendingSubmittedColumnLabel: string;
  /** Forklaring under tabellen, fx hvorfor der indberettes mere end lektionskravet. */
  footnote?: string;
};

const VARIANT_COPY = {
  pending: {
    description: "Samlet overblik over valgte lektioner før indsendelse.",
    transferredColumnLabel: "Overføres til næste periode",
  },
  submitted: {
    description:
      "Oversigt over de lektioner og overførsler, der indgår i den indsendte dokumentation.",
    transferredColumnLabel: "Overført til næste periode",
  },
} as const;

const InjunctionReportOverview = ({
  rows,
  variant,
  header,
  pendingSubmittedColumnLabel,
  footnote,
}: InjunctionReportOverviewProps) => {
  const copy = VARIANT_COPY[variant];
  const submittedColumnLabel =
    variant === "pending" ? pendingSubmittedColumnLabel : "Indberettet";
  const totals = rows.reduce(
    (summary, row) => ({
      totalLessons: summary.totalLessons + (row.totalLessons ?? 0),
      submittedLessons: summary.submittedLessons + row.submittedLessons,
      transferredToNext: summary.transferredToNext + row.transferredToNext,
    }),
    { totalLessons: 0, submittedLessons: 0, transferredToNext: 0 },
  );

  return (
    <RoundedCard
      bodyClassName="p-0"
      description={copy.description}
      header={header}
    >
      <div className="table-responsive">
        <table className="table align-middle mb-0 cle-report-detail__overview-table">
          <thead>
            <tr>
              <th scope="col">Kategori</th>
              {variant === "pending" ? (
                <th scope="col" className="text-end">
                  I alt
                </th>
              ) : null}
              <th scope="col" className="text-end">
                {submittedColumnLabel}
              </th>
              <th scope="col" className="text-end">
                {copy.transferredColumnLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              return (
                <tr key={row.key}>
                  <th scope="row">
                    <span>
                      {row.label}{" "}
                      {row.maxReported !== undefined ? (
                        <span className="cle-report-detail__category-limit">
                          (Max {row.maxReported} lektioner)
                        </span>
                      ) : null}
                    </span>
                    {row.canTransferLessons === false ? (
                      <span className="d-block text-muted tiny">
                        {NON_TRANSFERABLE_LABEL}
                      </span>
                    ) : null}
                    {row.droppedLessons ? (
                      <span className="d-block text-muted tiny">
                        {formatLessons(row.droppedLessons)} lektioner over
                        maksimum bortfalder
                      </span>
                    ) : null}
                  </th>
                  {variant === "pending" ? (
                    <td className="text-end">{row.totalLessons ?? 0}</td>
                  ) : null}
                  <td className="text-end">{row.submittedLessons}</td>
                  <td className="text-end">{row.transferredToNext}</td>
                </tr>
              );
            })}
            <tr className="cle-report-detail__overview-total-row">
              <th scope="row">I alt</th>
              {variant === "pending" ? (
                <td className="text-end">{totals.totalLessons}</td>
              ) : null}
              <td className="text-end">{totals.submittedLessons}</td>
              <td className="text-end">{totals.transferredToNext}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {footnote ? (
        <p className="text-muted small mb-0 px-3 py-3 border-top">{footnote}</p>
      ) : null}
    </RoundedCard>
  );
};

export default InjunctionReportOverview;
